import React, { useEffect, useState } from "react";
import { Button, Card, Col, Nav, ProgressBar, Row, Spinner, Alert, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import ReactApexChart from "react-apexcharts";
import { formatNumber, formatCurrency } from "../utils/formatters";
import { exportCAtoExcel } from "../utils/excelExport1";
import { generateCAPDF } from "../utils/pdfExport1";
import './Dashboard.css';

export default function FinanceMonitoring() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch("https://sawd.julesferry-ga.com/api/stats_dashboard.php", {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error("Erreur de chargement des données");
      
      const data = await response.json();
      console.log("📊 Données reçues:", data);
      setStats(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fonction d'export Excel avec données réelles
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      // Préparer les données réelles pour l'export
      const exportData = {
        // Informations générales
        periode: {
          annee: selectedYear,
          mois: new Date().toLocaleString('fr-FR', { month: 'long' })
        },
        
        // Totaux
        totaux: {
          ca_annuel: stats.totaux?.ca_annuel || 0,
          ca_mensuel: stats.totaux?.ca_mensuel || 0,
          nb_clients: stats.totaux?.nb_clients || 0,
          nb_fournisseurs: stats.totaux?.nb_fournisseurs || 0,
          nb_produits: stats.totaux?.nb_produits || 0,
          creances_total: stats.totaux?.creances_total || 0
        },
        
        // CA mensuel (données réelles du graphique)
        ca_mensuel: stats.ca_mensuel || Array(12).fill(0),
        
        // Mois avec leurs valeurs
        mois: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
               'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
        
        // Top produits (données réelles)
        top_produits: stats.top_produits || [],
        
        // Top clients (données réelles)
        top_clients: stats.top_clients || [],
        
        // Ventes par catégorie (données réelles)
        ventes_categories: stats.ventes_categories || [],
        
        // Revenus/Dépenses (données réelles)
        income_expenses: stats.income_expenses || [],
        
        // Stock alertes (données réelles)
        stock_alertes: stats.stock_alertes || [],
        
        // Marges (données réelles)
        profit_margins: stats.profit_margins || {},
        ratios: stats.ratios || {}
      };
      
      const success = await exportCAtoExcel(exportData, selectedYear);
      
      if (!success) {
        alert("Erreur lors de l'export Excel");
      }
    } catch (error) {
      console.error("Erreur export Excel:", error);
      alert("Erreur lors de l'export Excel: " + error.message);
    } finally {
      setExporting(false);
    }
  };

  // ✅ Fonction d'export PDF avec données réelles
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      // Préparer les données réelles pour l'export PDF
      const exportData = {
        // Informations générales
        titre: "Rapport Financier",
        periode: {
          annee: selectedYear,
          mois: new Date().toLocaleString('fr-FR', { month: 'long' })
        },
        
        // Totaux
        totaux: {
          ca_annuel: stats.totaux?.ca_annuel || 0,
          ca_mensuel: stats.totaux?.ca_mensuel || 0,
          nb_clients: stats.totaux?.nb_clients || 0,
          nb_fournisseurs: stats.totaux?.nb_fournisseurs || 0,
          nb_produits: stats.totaux?.nb_produits || 0,
          creances_total: stats.totaux?.creances_total || 0
        },
        
        // Données mensuelles détaillées (calculées à partir des données réelles)
        mensuel_detail: generateMonthlyDetailFromRealData(stats),
        
        // Top produits (données réelles)
        top_produits: stats.top_produits || [],
        
        // Top clients (données réelles)
        top_clients: stats.top_clients || [],
        
        // Graphiques
        ca_mensuel: stats.ca_mensuel || Array(12).fill(0),
        ventes_categories: stats.ventes_categories || [],
        income_expenses: stats.income_expenses || [],
        
        // Alertes stock
        stock_alertes: stats.stock_alertes || []
      };
      
      await generateCAPDF(exportData, selectedYear);
    } catch (error) {
      console.error("Erreur export PDF:", error);
      alert("Erreur lors de l'export PDF: " + error.message);
    } finally {
      setExporting(false);
    }
  };

  // ✅ Générer des données mensuelles détaillées à partir des données réelles
  const generateMonthlyDetailFromRealData = (data) => {
    if (!data?.ca_mensuel || data.ca_mensuel.length === 0) return [];
    
    const moisNoms = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    
    return data.ca_mensuel.map((montant, index) => {
      // Calculer le nombre de jours dans le mois
      const joursDansMois = new Date(selectedYear, index + 1, 0).getDate();
      
      return {
        mois: moisNoms[index],
        montant: montant,
        jours: joursDansMois,
        moyenne_journaliere: joursDansMois > 0 ? Math.round(montant / joursDansMois) : 0,
        pourcentage_annuel: data.totaux?.ca_annuel > 0 
          ? ((montant / data.totaux?.ca_annuel) * 100).toFixed(1) 
          : 0
      };
    });
  };

  // Calculer le nombre total de ventes (factures) à partir des données disponibles
  const getTotalVentes = () => {
    if (!stats) return 0;
    
    if (stats.ca_annuel?.[0]?.nombre_factures) {
      return stats.ca_annuel[0].nombre_factures;
    }
    
    if (stats.top_clients) {
      return stats.top_clients.reduce((acc, client) => acc + (client.nb_factures || 0), 0);
    }
    
    return 0;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Chargement des données...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <Alert variant="danger">
          <Alert.Heading>Erreur</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </div>
    );
  }

  const totalVentes = getTotalVentes();

  // Données pour les graphiques
  const seriesCA = [{
    name: 'Chiffre d\'affaires',
    data: stats.ca_mensuel || Array(12).fill(0)
  }];

  const optionsCA = {
    chart: { type: 'area', height: 350 },
    colors: ['#4361ee'],
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.2
      }
    },
    xaxis: {
      categories: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc']
    },
    yaxis: {
      labels: {
        formatter: (value) => formatCurrency(value)
      }
    },
    tooltip: {
      y: {
        formatter: (value) => formatCurrency(value)
      }
    }
  };

  const seriesIncomeExpenses = [
    {
      name: 'Revenus',
      type: 'column',
      data: stats?.income_expenses?.map(item => parseFloat(item.revenus)) || []
    },
    {
      name: 'Dépenses',
      type: 'column',
      data: stats?.income_expenses?.map(item => parseFloat(item.depenses)) || []
    }
  ];

  const optionsIncomeExpenses = {
    chart: { stacked: true, toolbar: { show: false } },
    colors: ['#4361ee', '#f59e0b'],
    plotOptions: {
      bar: {
        columnWidth: '60%',
        endingShape: 'rounded'
      }
    },
    xaxis: {
      categories: stats?.income_expenses?.map(item => {
        const [year, month] = item.mois.split('-');
        const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
        return `${months[parseInt(month)-1]} ${year}`;
      }) || []
    },
    yaxis: {
      labels: {
        formatter: (value) => formatCurrency(value)
      }
    }
  };

  const seriesCategories = stats?.ventes_categories?.map(item => parseFloat(item.montant_total)) || [];
  const optionsDonut = {
    chart: { type: 'donut' },
    labels: stats?.ventes_categories?.map(item => item.nom_categorie || 'Sans catégorie') || [],
    colors: ['#4361ee', '#06b6d4', '#10b981', '#f59e0b', '#ef4444'],
    legend: { position: 'bottom' },
    tooltip: {
      y: {
        formatter: (value) => formatCurrency(value)
      }
    }
  };

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {/* En-tête avec boutons d'export */}
        <div className="d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">Tableau de bord</Link></li>
              <li className="breadcrumb-item active" aria-current="page">Vue d'ensemble</li>
            </ol>
            <h4 className="main-title mb-0">Tableau de bord financier</h4>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <Button 
              variant="outline-primary" 
              className="d-flex align-items-center gap-2"
              onClick={handleExportExcel}
              disabled={exporting}
            >
              {exporting ? (
                <Spinner size="sm" className="me-2" />
              ) : (
                <i className="ri-file-excel-line fs-18 lh-1"></i>
              )}
              Export Excel
            </Button> 
            
            <Button 
              variant="outline-danger" 
              className="d-flex align-items-center gap-2"
              onClick={handleExportPDF}
              disabled={exporting}
            >
              {exporting ? (
                <Spinner size="sm" className="me-2" />
              ) : (
                <i className="ri-file-pdf-line fs-18 lh-1"></i>
              )} 
              Export PDF
            </Button> 
          </div>
        </div>

        {/* KPI Cards */}
        <Row className="g-4 mb-5">
          {/* Carte CA Annuel */}
          <Col sm="6" xl="3">
            <Card className="kpi-card border-0 h-100 animate__animated animate__fadeInUp" style={{ animationDelay: '0.1s' }}>
              <Card.Body className="p-4">
                <div className="d-flex align-items-center">
                  <div className="kpi-icon-wrapper bg-gradient-primary me-3">
                    <i className="ri-money-euro-circle-line fs-24"></i>
                  </div>
                  <div className="flex-grow-1">
                    <span className="kpi-label">CA annuel</span>
                    <h4 className="kpi-value mb-1">{formatCurrency(parseFloat(stats.totaux?.ca_annuel) || 0)}</h4>
                    <div className="kpi-trend text-success">
                      <i className="ri-arrow-up-line me-1"></i>
                      <span>{stats.ca_annuel?.[0]?.nombre_factures || totalVentes} factures</span>
                    </div>
                  </div>
                </div>
                <div className="kpi-footer mt-3 pt-2 border-top">
                  <small className="text-muted">
                    <i className="ri-calendar-line me-1"></i>
                    Année {stats.ca_annuel?.[0]?.annee || 2026}
                  </small>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Carte Ventes */}
          <Col sm="6" xl="3">
            <Card className="kpi-card border-0 h-100 animate__animated animate__fadeInUp" style={{ animationDelay: '0.2s' }}>
              <Card.Body className="p-4">
                <div className="d-flex align-items-center">
                  <div className="kpi-icon-wrapper bg-gradient-success me-3">
                    <i className="ri-shopping-bag-3-line fs-24"></i>
                  </div>
                  <div className="flex-grow-1">
                    <span className="kpi-label">Ventes totales</span>
                    <h3 className="kpi-value mb-1">{totalVentes}</h3>
                    <div className="d-flex justify-content-between align-items-center flex-wrap">
                      <span className="text-muted small">CA Mensuel: {formatCurrency(parseFloat(stats.totaux?.ca_mensuel) || 0)}</span>
                      <Badge bg="success" className="mt-1 mt-sm-0">
                        <i className="ri-bar-chart-line me-1"></i>
                        {stats.ventes_categories?.length || 0} catégories
                      </Badge>
                    </div>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="d-flex justify-content-between small mb-1">
                    <span>Objectif mensuel (5M)</span>
                    <span className="fw-bold">{Math.round((parseFloat(stats.totaux?.ca_mensuel) / 5000000) * 100)}%</span>
                  </div>
                  <ProgressBar 
                    now={Math.min((parseFloat(stats.totaux?.ca_mensuel) / 5000000) * 100, 100)} 
                    variant="success" 
                    style={{ height: '6px', borderRadius: '3px' }}
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Carte Clients */}
          <Col sm="6" xl="3">
            <Card className="kpi-card border-0 h-100 animate__animated animate__fadeInUp" style={{ animationDelay: '0.3s' }}>
              <Card.Body className="p-4">
                <div className="d-flex align-items-center">
                  <div className="kpi-icon-wrapper bg-gradient-warning me-3">
                    <i className="ri-group-line fs-24"></i>
                  </div>
                  <div className="flex-grow-1">
                    <span className="kpi-label">Clients actifs</span>
                    <h3 className="kpi-value mb-1">{stats.totaux?.nb_clients || 0}</h3>
                    <div className="d-flex justify-content-between flex-wrap">
                      <span className="text-muted small">Fournisseurs: {stats.totaux?.nb_fournisseurs || 0}</span>
                      <span className="badge bg-warning-soft text-warning">
                        <i className="ri-team-line me-1"></i>
                        Top {stats.top_clients?.length || 0}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="d-flex justify-content-between small mb-1">
                    <span>Créances clients</span>
                    <span className="fw-bold text-warning">{formatCurrency(parseFloat(stats.totaux?.creances_total) || 0)}</span>
                  </div>
                  <ProgressBar 
                    now={Math.min((parseFloat(stats.totaux?.creances_total) / 1000000) * 10, 100)} 
                    variant="warning" 
                    style={{ height: '4px' }} 
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Carte Stock */}
          <Col sm="6" xl="3">
            <Card className="kpi-card border-0 h-100 animate__animated animate__fadeInUp" style={{ animationDelay: '0.4s' }}>
              <Card.Body className="p-4">
                <div className="d-flex align-items-center">
                  <div className="kpi-icon-wrapper bg-gradient-info me-3">
                    <i className="ri-stack-line fs-24"></i>
                  </div>
                  <div className="flex-grow-1">
                    <span className="kpi-label">Produits en stock</span>
                    <h3 className="kpi-value mb-1">{stats.totaux?.nb_produits || 0}</h3>
                    <div className="d-flex justify-content-between flex-wrap">
                      <span className="text-muted small">Top ventes: {stats.top_produits?.length || 0}</span>
                      {stats.stock_alertes?.length > 0 && (
                        <Badge bg="danger" pill>
                          <i className="ri-alert-line me-1"></i>
                          {stats.stock_alertes.length} alerte(s)
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-3">
                  {stats.stock_alertes?.length > 0 ? (
                    <Link to="/stock/alertes" className="btn btn-outline-danger btn-sm w-100">
                      <i className="ri-alert-line me-2"></i>
                      Voir les alertes ({stats.stock_alertes.length})
                    </Link>
                  ) : (
                    <div className="text-center text-success p-2 bg-light rounded">
                      <i className="ri-checkbox-circle-line me-1"></i>
                      Stock normal
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Graphiques principaux */}
        <Row className="g-4 mb-5">
          <Col xl="8">
            <Card className="card-one border-0 shadow-sm">
              <Card.Header className="bg-transparent border-0 d-flex align-items-center">
                <Card.Title as="h6" className="mb-0">Évolution du chiffre d'affaires</Card.Title>
                <Nav className="nav-icon ms-auto">
                  <Nav.Link 
                    onClick={() => setSelectedYear(2026)}
                    className={selectedYear === 2026 ? 'active' : ''}
                  >
                    2026
                  </Nav.Link>
                </Nav>
              </Card.Header>
              <Card.Body>
                <ReactApexChart 
                  series={seriesCA} 
                  options={optionsCA} 
                  type="area" 
                  height={350} 
                />
              </Card.Body>
            </Card>
          </Col>

          <Col xl="4">
            <Card className="card-one border-0 shadow-sm h-100">
              <Card.Header className="bg-transparent border-0">
                <Card.Title as="h6" className="mb-0">Répartition des ventes</Card.Title>
              </Card.Header>
              <Card.Body className="d-flex flex-column justify-content-center">
                <ReactApexChart 
                  series={seriesCategories} 
                  options={optionsDonut} 
                  type="donut" 
                  height={300} 
                />
                <div className="text-center mt-3">
                  <small className="text-muted">
                    Total: {formatCurrency(seriesCategories.reduce((a, b) => a + b, 0))}
                  </small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Revenus vs Dépenses */}
        {stats.income_expenses && stats.income_expenses.length > 0 && (
          <Row className="g-4 mb-5">
            <Col xl="7">
              <Card className="card-one border-0 shadow-sm">
                <Card.Header className="bg-transparent border-0">
                  <Card.Title as="h6" className="mb-0">Revenus vs Dépenses</Card.Title>
                </Card.Header>
                <Card.Body>
                  <ReactApexChart 
                    series={seriesIncomeExpenses} 
                    options={optionsIncomeExpenses} 
                    type="bar" 
                    height={350} 
                  />
                </Card.Body>
              </Card>
            </Col>

            <Col xl="5">
              <Card className="card-one border-0 shadow-sm">
                <Card.Header className="bg-transparent border-0">
                  <Card.Title as="h6" className="mb-0">Marges bénéficiaires</Card.Title>
                </Card.Header>
                <Card.Body>
                  <div className="mb-4">
                    <div className="d-flex justify-content-between mb-1">
                      <span>Marge brute</span>
                      <span className="fw-bold">{stats.profit_margins?.gross || 0}%</span>
                    </div>
                    <ProgressBar now={stats.profit_margins?.gross || 0} className="mb-3" />
                    
                    <div className="d-flex justify-content-between mb-1">
                      <span>Marge opérationnelle</span>
                      <span className="fw-bold">{stats.profit_margins?.operating || 0}%</span>
                    </div>
                    <ProgressBar now={stats.profit_margins?.operating || 0} variant="info" className="mb-3" />
                    
                    <div className="d-flex justify-content-between mb-1">
                      <span>Marge nette</span>
                      <span className="fw-bold">{stats.profit_margins?.net || 0}%</span>
                    </div>
                    <ProgressBar now={stats.profit_margins?.net || 0} variant="success" />
                  </div>

                  <hr />

                  <Row>
                    <Col xs="6">
                      <div className="text-center p-3 bg-light rounded">
                        <h5 className="mb-1 text-primary">{stats.ratios?.quick_ratio || 0}</h5>
                        <small>Quick Ratio</small>
                      </div>
                    </Col>
                    <Col xs="6">
                      <div className="text-center p-3 bg-light rounded">
                        <h5 className="mb-1 text-success">{stats.ratios?.current_ratio || 0}</h5>
                        <small>Current Ratio</small>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Top produits et top clients */}
        <Row className="g-4 mb-4">
          <Col xl="6">
            <Card className="card-one border-0 shadow-sm">
              <Card.Header className="bg-transparent border-0 d-flex justify-content-between align-items-center">
                <Card.Title as="h6" className="mb-0">Top {stats.top_produits?.length || 3} Produits</Card.Title>
                <span className="badge bg-primary">Ventes</span>
              </Card.Header>
              <Card.Body>
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Référence</th>
                        <th>Désignation</th>
                        <th className="text-end">Quantité</th>
                        <th className="text-end">Montant</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.top_produits?.map((produit, index) => (
                        <tr key={index}>
                          <td><span className="badge bg-light text-dark">{produit.reference}</span></td>
                          <td className="fw-semibold">{produit.designation}</td>
                          <td className="text-end">{formatNumber(parseInt(produit.quantite_vendue))}</td>
                          <td className="text-end text-primary fw-bold">{formatCurrency(parseFloat(produit.montant_total))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xl="6">
            <Card className="card-one border-0 shadow-sm">
              <Card.Header className="bg-transparent border-0 d-flex justify-content-between align-items-center">
                <Card.Title as="h6" className="mb-0">Top {stats.top_clients?.length || 3} Clients</Card.Title>
                <span className="badge bg-success">Chiffre d'affaires</span>
              </Card.Header>
              <Card.Body>
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Code</th>
                        <th>Client</th>
                        <th className="text-end">Factures</th>
                        <th className="text-end">Total achats</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.top_clients?.map((client, index) => (
                        <tr key={index}>
                          <td><span className="badge bg-primary">{client.code_client}</span></td>
                          <td className="fw-semibold">{client.raison_sociale}</td>
                          <td className="text-end">{client.nb_factures}</td>
                          <td className="text-end text-success fw-bold">{formatCurrency(parseFloat(client.total_achats))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Styles CSS */}
        <style>{`
          .kpi-card {
            border-radius: 20px;
            background: white;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
            transition: all 0.3s ease;
            overflow: hidden;
            position: relative;
          }

          .kpi-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 15px 40px rgba(67, 97, 238, 0.15);
          }

          .kpi-card::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 4px;
            background: linear-gradient(90deg, #4361ee, #06b6d4);
          }

          .kpi-icon-wrapper {
            width: 60px;
            height: 60px;
            border-radius: 18px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 24px;
            box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
          }

          .bg-gradient-primary {
            background: linear-gradient(135deg, #4361ee, #3a56d4);
          }

          .bg-gradient-success {
            background: linear-gradient(135deg, #10b981, #059669);
          }

          .bg-gradient-warning {
            background: linear-gradient(135deg, #f59e0b, #d97706);
          }

          .bg-gradient-info {
            background: linear-gradient(135deg, #06b6d4, #0891b2);
          }

          .bg-success-soft {
            background: rgba(16, 185, 129, 0.1);
            color: #10b981;
          }

          .bg-warning-soft {
            background: rgba(245, 158, 11, 0.1);
            color: #f59e0b;
          }

          .kpi-label {
            font-size: 0.85rem;
            font-weight: 500;
            color: #6b7280;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .kpi-value {
            font-size: 1.6rem;
            font-weight: 700;
            color: #1e293b;
            line-height: 1.2;
          }

          .kpi-trend {
            font-size: 0.85rem;
            display: flex;
            align-items: center;
          }

          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .animate__animated {
            animation-duration: 0.6s;
            animation-fill-mode: both;
          }

          .animate__fadeInUp {
            animation-name: fadeInUp;
          }

          /* Styles pour les boutons d'export */
          .btn-outline-primary:hover i,
          .btn-outline-danger:hover i,
          .btn-primary:hover i {
            color: white;
          }
        `}</style>

        <Footer />
      </div>
    </React.Fragment>
  );
}